/**
 * Single source of truth for network connectivity (browser + Capacitor Network).
 * No setInterval polling — event-driven only.
 */

import { isNativePlatform } from '../providerPush';

const listeners = new Set();
const reconnectListeners = new Set();

let initialized = false;
let capacitorListener = null;

const state = {
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  connectionState: typeof navigator !== 'undefined' && navigator.onLine ? 'online' : 'offline',
  lastOnlineAt: typeof navigator !== 'undefined' && navigator.onLine ? Date.now() : null,
  lastOfflineAt: typeof navigator !== 'undefined' && !navigator.onLine ? Date.now() : null,
  ready: false,
};

function notify() {
  const snapshot = getNetworkState();
  listeners.forEach((fn) => fn(snapshot));
}

function emitReconnect() {
  reconnectListeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore listener errors */
    }
  });
}

function applyOnline(connectionType) {
  const wasOffline = !state.isOnline;
  state.isOnline = true;
  state.connectionState = connectionType || 'online';
  state.lastOnlineAt = Date.now();
  notify();
  if (wasOffline) emitReconnect();
}

function applyOffline() {
  if (!state.isOnline) return;
  state.isOnline = false;
  state.connectionState = 'offline';
  state.lastOfflineAt = Date.now();
  notify();
}

function onBrowserOnline() {
  applyOnline('online');
}

function onBrowserOffline() {
  applyOffline();
}

function applyCapacitorStatus(status) {
  if (!status) return;
  if (status.connected) {
    applyOnline(status.connectionType || 'online');
  } else {
    applyOffline();
  }
}

export function getNetworkState() {
  return { ...state };
}

export function subscribeNetwork(listener) {
  listeners.add(listener);
  listener(getNetworkState());
  return () => listeners.delete(listener);
}

export function onReconnect(listener) {
  reconnectListeners.add(listener);
  return () => reconnectListeners.delete(listener);
}

export async function initNetworkConnectivity() {
  if (initialized) return getNetworkState();

  if (typeof window !== 'undefined') {
    window.addEventListener('online', onBrowserOnline);
    window.addEventListener('offline', onBrowserOffline);
  }

  if (isNativePlatform()) {
    try {
      const { Network } = await import('@capacitor/network');
      const status = await Network.getStatus();
      applyCapacitorStatus(status);
      const handle = await Network.addListener('networkStatusChange', applyCapacitorStatus);
      capacitorListener = handle;
    } catch {
      /* fall back to browser events */
    }
  } else if (typeof navigator !== 'undefined') {
    if (navigator.onLine) {
      applyOnline('online');
    } else {
      applyOffline();
    }
  }

  state.ready = true;
  initialized = true;
  notify();
  return getNetworkState();
}

/** Test helper — reset module state. */
export function __resetNetworkConnectivityForTests() {
  if (typeof window !== 'undefined') {
    window.removeEventListener('online', onBrowserOnline);
    window.removeEventListener('offline', onBrowserOffline);
  }
  if (capacitorListener?.remove) {
    capacitorListener.remove();
  }
  capacitorListener = null;
  listeners.clear();
  reconnectListeners.clear();
  initialized = false;
  state.isOnline = true;
  state.connectionState = 'online';
  state.lastOnlineAt = Date.now();
  state.lastOfflineAt = null;
  state.ready = false;
}
